import React, { useState, useMemo, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import PropertyCard from '../components/PropertyCard';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Search, SlidersHorizontal, ArrowLeft } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = typeof window !== 'undefined' ? window.location.origin : process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Properties = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState('');
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  
  // Filters from URL
  const categoryFilter = searchParams.get('category') || 'all';
  const stateFilter = searchParams.get('state') || 'all';
  const minPrice = searchParams.get('minPrice') || '';
  const maxPrice = searchParams.get('maxPrice') || '';

  useEffect(() => {
    const fetchProperties = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${API}/properties`);
        setProperties(response.data);
      } catch (error) {
        console.error('Error fetching properties:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProperties();
  }, []);

  // Get unique states from properties
  const availableStates = useMemo(() => {
    const states = [...new Set(properties.map(p => p.state))].filter(Boolean);
    return states.sort();
  }, [properties]);

  // Get price range
  const priceRange = useMemo(() => {
    if (properties.length === 0) return { min: 0, max: 20000000 };
    const prices = properties.map(p => p.price);
    return {
      min: Math.floor(Math.min(...prices) / 100000) * 100000,
      max: Math.ceil(Math.max(...prices) / 100000) * 100000
    };
  }, [properties]);

  const filteredProperties = useMemo(() => {
    return properties.filter((property) => {
      // Category filter
      if (categoryFilter !== 'all') {
        const categories = property.categories || [];
        if (!categories.includes(categoryFilter)) return false;
      }

      // State filter
      if (stateFilter !== 'all' && property.state !== stateFilter) return false;

      // Price range filter
      if (minPrice && property.price < parseInt(minPrice)) return false;
      if (maxPrice && property.price > parseInt(maxPrice)) return false;

      // Search filter
      const matchesSearch = property.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           property.location.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           (property.description && property.description.toLowerCase().includes(searchTerm.toLowerCase()));
      
      return matchesSearch;
    });
  }, [properties, categoryFilter, stateFilter, minPrice, maxPrice, searchTerm]);

  const updateFilter = (key, value) => {
    const newParams = new URLSearchParams(searchParams);
    if (value === 'all' || value === '') {
      newParams.delete(key);
    } else {
      newParams.set(key, value);
    }
    setSearchParams(newParams);
  };

  const clearFilters = () => {
    setSearchParams({});
    setSearchTerm('');
  };

  const getCategoryTitle = () => {
    switch(categoryFilter) {
      case 'residential': return 'Propiedades Residenciales';
      case 'commercial': return 'Propiedades Comerciales';
      case 'land': return 'Terrenos';
      default: return 'Todas las Propiedades';
    }
  };

  const getCategoryDescription = () => {
    switch(categoryFilter) {
      case 'residential': return 'Casas, departamentos y terrenos ideales para vivir';
      case 'commercial': return 'Edificios, hoteles y terrenos perfectos para tu negocio';
      case 'land': return 'Terrenos con gran potencial de desarrollo';
      default: return 'Explora nuestra selección completa de propiedades disponibles';
    }
  };

  const activeFiltersCount = [categoryFilter, stateFilter, minPrice, maxPrice].filter(f => f && f !== 'all').length;

  return (
    <div className="min-h-screen pt-16 md:pt-20">
      {/* Header */}
      <section className="bg-gradient-to-br from-gray-900 to-gray-800 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Breadcrumb */}
          {categoryFilter !== 'all' && (
            <Link 
              to="/propiedades"
              className="inline-flex items-center space-x-2 text-golden-400 hover:text-golden-300 mb-4 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="text-sm">Volver a todas las propiedades</span>
            </Link>
          )}
          
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            {getCategoryTitle()}
          </h1>
          <p className="text-xl text-gray-300">
            {getCategoryDescription()}
          </p>
        </div>
      </section>

      {/* Filters and Results */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Filter Bar */}
          <div className="bg-white rounded-lg shadow-md p-6 mb-8">
            <div className="flex flex-col lg:flex-row lg:items-center gap-4">
              {/* Search */}
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="text"
                    placeholder="Buscar por título, ubicación o descripción..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-golden-500 focus:border-transparent"
                  />
                </div>
              </div>

              {/* Filters Toggle */}
              <Button
                onClick={() => setShowFilters(!showFilters)}
                variant="outline"
                className="flex items-center space-x-2"
              >
                <SlidersHorizontal className="w-4 h-4" />
                <span>Filtros</span>
                {activeFiltersCount > 0 && (
                  <span className="bg-golden-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                    {activeFiltersCount}
                  </span>
                )}
              </Button>
            </div>

            {/* Advanced Filters */}
            {showFilters && (
              <div className="mt-6 pt-6 border-t border-gray-200">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* State Filter */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Estado
                    </label>
                    <Select value={stateFilter} onValueChange={(value) => updateFilter('state', value)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Todos los estados" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todos los estados</SelectItem>
                        {availableStates.map(state => (
                          <SelectItem key={state} value={state}>{state}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Min Price Filter */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Precio Mínimo
                    </label>
                    <input
                      type="number"
                      value={minPrice}
                      onChange={(e) => updateFilter('minPrice', e.target.value)}
                      placeholder={`$${priceRange.min.toLocaleString()}`}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-golden-500 focus:border-transparent"
                      step="100000"
                    />
                  </div>

                  {/* Max Price Filter */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Precio Máximo
                    </label>
                    <input
                      type="number"
                      value={maxPrice}
                      onChange={(e) => updateFilter('maxPrice', e.target.value)}
                      placeholder={`$${priceRange.max.toLocaleString()}`}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-golden-500 focus:border-transparent"
                      step="100000"
                    />
                  </div>
                </div>

                {/* Clear Filters */}
                {activeFiltersCount > 0 && (
                  <div className="mt-4 flex justify-end">
                    <Button
                      onClick={clearFilters}
                      variant="ghost"
                      className="text-golden-600 hover:text-golden-700"
                    >
                      Limpiar filtros
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Results Count */}
          <div className="mb-6">
            <p className="text-gray-600">
              {loading ? 'Cargando...' : `${filteredProperties.length} ${filteredProperties.length === 1 ? 'propiedad encontrada' : 'propiedades encontradas'}`}
            </p>
          </div>

          {/* Properties Grid */}
          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-golden-600"></div>
            </div>
          ) : filteredProperties.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-500 text-lg mb-4">No se encontraron propiedades con los filtros seleccionados</p>
              <Button onClick={clearFilters} className="bg-golden-600 hover:bg-golden-700">
                Ver todas las propiedades
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredProperties.map((property) => (
                <PropertyCard key={property.id} property={property} />
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Properties;
